import React, { Children, HTMLAttributes, PropsWithChildren, ReactElement, ReactNode, isValidElement } from 'react'

import { cn } from '@frame/lib/utils/cn'
import { DataAttribute } from '@frame/types'

export type RadioGroupOption = { id: string; value: string }

type Props<T = string> = {
  selected: string | null
  name?: string
  className?: HTMLAttributes<HTMLFieldSetElement>['className']
  children: ReactElement<RadioGroupOption>[] | ReactNode
  onChange: (value: T) => void
}

export function RadioGroup<T = string>({ children, selected, name = 'options', className, onChange }: Props<T>) {
  return (
    <fieldset
      data-cy={DataAttribute.RADIO_GROUP}
      className={cn('flex flex-col gap-4', className)}
      role="radiogroup"
      aria-label={name}
    >
      {Children.map(children, (child) => {
        if (!isValidElement<RadioGroupOption>(child)) return null

        const { id, value } = child.props
        const checked = selected === value

        return (
          <div key={id} role="radio" aria-checked={checked}>
            <label
              key={id}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer select-none items-center gap-3 rounded-md border border-moss-300 bg-white p-4 focus-visible:outline-electric-650',
                checked && 'bg-electric-200'
              )}
            >
              <div
                className={cn(
                  'h-6 w-6 flex-shrink-0 rounded-full border-[3px] border-moss-500 bg-white transition-all ',
                  checked && 'border-[10px] border-electric-650'
                )}
              />
              {child}
            </label>
            <input
              type="radio"
              name={name}
              id={id}
              value={String(value)}
              checked={checked}
              onChange={() => onChange(value as T)}
              className="sr-only"
            />
          </div>
        )
      })}
    </fieldset>
  )
}

export function RadioOption({ children }: PropsWithChildren<RadioGroupOption | null>) {
  return <>{children}</>
}
